"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";

import { REWARDS_CLAIM_TOAST_SEEN_KEY } from "@/constants/local-storage-keys";
import { useRewardsList } from "@/contexts/queries/rewards/use-rewards-list";
import { useLocalStorage } from "@/hooks/storage";
import { buildStorageKey, getNetworkForStorage } from "@/hooks/storage/use-local-storage";
import { useAuthState } from "@/hooks/use-auth-state";
import { linkStyles } from "@/recipes/link";
import { NumberFormatter } from "@/utils/number-formatter";

import { toast } from "../toast";

const TOAST_ID = "rewards-claim";

export function RewardsClaimToastTrigger() {
  const t = useTranslations("rewards.toast");
  const { accountAddress, state } = useAuthState();
  const { data, isLoading: isRewardsLoading } = useRewardsList(accountAddress);

  const { totalUsd, claimableKey } = useMemo(() => {
    const claimable = data.rewards.filter((r) => r.status === "claimable");
    const totalUsd = claimable.reduce((sum, r) => sum + r.rewardAmountUsd, 0);
    return {
      totalUsd,
      claimableKey: `${[...claimable.map((r) => r.id)].sort().join(",")}|${totalUsd}`,
    };
  }, [data.rewards]);

  const storageKey = buildStorageKey(
    accountAddress
      ? `${REWARDS_CLAIM_TOAST_SEEN_KEY}-${accountAddress.toLowerCase()}`
      : REWARDS_CLAIM_TOAST_SEEN_KEY,
    { network: getNetworkForStorage() },
  );
  const [seenKey, setSeenKey, isLoading] = useLocalStorage<string>(storageKey, "");

  const isReady = state !== "LOADING" && !isLoading && !isRewardsLoading && !!accountAddress;

  useEffect(() => () => toast.dismiss(TOAST_ID), []);

  useEffect(() => {
    if (!isReady) return;

    const hasNothingToClaim = totalUsd <= 0;
    const alreadyToastedThisSet = seenKey === claimableKey;

    if (hasNothingToClaim) {
      if (seenKey) {
        toast.dismiss(TOAST_ID);
        setSeenKey("");
      }
      return;
    }

    if (alreadyToastedThisSet) return;

    const formattedAmount = NumberFormatter.usdc(totalUsd);
    queueMicrotask(() => {
      toast({
        id: TOAST_ID,
        accountScoped: true,
        variant: "info",
        title: t("unclaimedTitle"),
        description: () => (
          <span>
            {t.rich("unclaimedDescription", {
              amount: formattedAmount,
              link: (chunks) => (
                <Link
                  href="/rewards"
                  className={linkStyles}
                  onClick={() => toast.dismiss(TOAST_ID)}
                >
                  {chunks}
                </Link>
              ),
            })}
          </span>
        ),
        duration: Infinity,
      });
      setSeenKey(claimableKey);
    });
  }, [isReady, totalUsd, claimableKey, seenKey, setSeenKey, t]);

  return null;
}
